import type { Svg } from "@svgdotjs/svg.js";
import { HORIZONTAL_PADDING_PX, VERTICAL_PADDING_PX, COLUMN_GAP_PX, ROW_GAP_PX, SECTION_STROKE_PX } from "../constant";
import type { SectionNomenclature, SideTypeWithoutTop } from "../types";
import { drawDimensionInMillimeters } from "./dimensions";
import { normalizeToArray } from "../utils/math";

export const drawTopShelves = (
    svg: Svg,
    pixelsPerMillimeter: number,
    roomLengthMm: number,
    roomDepthMm: number,
    sections: (SectionNomenclature & { offsetCeiling: number })[]
) => {
    const originX = HORIZONTAL_PADDING_PX;
    const originY = VERTICAL_PADDING_PX;
    const sectionsBySide = (side: SideTypeWithoutTop) => sections.filter((section) => section.side === side);
    const renderedDepthsBySide: Record<string, number[]> = {};

    const ensureDepthDimension = (
        sideKey: string,
        xCenterMm: number,
        yFromMm: number,
        yToMm: number,
        depthMm: number
    ) => {
        if (!renderedDepthsBySide?.[sideKey]?.includes(depthMm)) {
            drawDimensionInMillimeters(
                svg,
                xCenterMm,
                yFromMm,
                xCenterMm,
                yToMm,
                0,
                `${depthMm}`,
                pixelsPerMillimeter,
                {
                    fontPx: 6,
                }
            );
            renderedDepthsBySide[sideKey] = [...(renderedDepthsBySide[sideKey] ?? []), depthMm];
        }
    };

    const strokeRectangle = (xPx: number, yPx: number, widthPx: number, heightPx: number) => {
        svg.rect(widthPx, heightPx).move(xPx, yPx).fill("none").stroke({ width: SECTION_STROKE_PX, color: "#a64022" });
    };

    sectionsBySide("front").forEach((section, sectionIndex) => {
        const startXpx = originX + (section.offset + sectionIndex * COLUMN_GAP_PX) * pixelsPerMillimeter;
        normalizeToArray(section.items).forEach((shelf) => {
            const shelfWidthMm = Number(shelf?.width) || 0;
            const shelfDepthMm = Math.max(Number(shelf?.depth) || 0, 0);
            if (shelfWidthMm <= 0 || shelfDepthMm <= 0) return;
            ensureDepthDimension(
                section.side,
                startXpx / pixelsPerMillimeter + shelfWidthMm / 2,
                originY / pixelsPerMillimeter + shelfDepthMm,
                originY / pixelsPerMillimeter,
                shelfDepthMm
            );
            strokeRectangle(startXpx, originY, shelfWidthMm * pixelsPerMillimeter, shelfDepthMm * pixelsPerMillimeter);
        });
    });

    sectionsBySide("back").forEach((section, sectionIndex) => {
        const bottomYpx = originY + roomDepthMm * pixelsPerMillimeter;
        normalizeToArray(section.items).forEach((shelf) => {
            const shelfWidthMm = Number(shelf?.width) || 0;
            const shelfDepthMm = Math.max(Number(shelf?.depth) || 0, 0);
            if (shelfWidthMm <= 0 || shelfDepthMm <= 0) return;
            const startXpx =
                originX +
                (roomLengthMm - section.offset - section.width - sectionIndex * COLUMN_GAP_PX) * pixelsPerMillimeter -
                (shelfWidthMm > section.width ? (shelfWidthMm * pixelsPerMillimeter) / 2 : 0);
            ensureDepthDimension(
                section.side,
                startXpx / pixelsPerMillimeter + shelfWidthMm / 2,
                bottomYpx / pixelsPerMillimeter,
                bottomYpx / pixelsPerMillimeter - shelfDepthMm,
                shelfDepthMm
            );
            strokeRectangle(
                startXpx,
                bottomYpx - shelfDepthMm * pixelsPerMillimeter,
                shelfWidthMm * pixelsPerMillimeter,
                shelfDepthMm * pixelsPerMillimeter
            );
        });
    });

    sectionsBySide("left").forEach((section, sectionIndex) => {
        normalizeToArray(section.items).forEach((shelf) => {
            const shelfWidthMm = Number(shelf?.width) || 0;
            const shelfDepthMm = Math.max(Number(shelf?.depth) || 0, 0);
            if (shelfWidthMm <= 0 || shelfDepthMm <= 0) return;

            const startYpx =
                VERTICAL_PADDING_PX +
                (roomDepthMm - section.offset - section.width - sectionIndex * ROW_GAP_PX) * pixelsPerMillimeter +
                (shelfWidthMm > section.width ? (-shelfWidthMm * pixelsPerMillimeter) / 2 : 0);

            const yCenterMm = startYpx / pixelsPerMillimeter + shelfWidthMm / 2;
            const leftWallMm = HORIZONTAL_PADDING_PX / pixelsPerMillimeter;

            if (!renderedDepthsBySide?.[section.side]?.includes(shelfDepthMm)) {
                drawDimensionInMillimeters(
                    svg,
                    leftWallMm,
                    yCenterMm,
                    leftWallMm + shelfDepthMm,
                    yCenterMm,
                    0,
                    `${shelfDepthMm}`,
                    pixelsPerMillimeter,
                    { fontPx: 6 }
                );
                renderedDepthsBySide[section.side] = [...(renderedDepthsBySide[section.side] ?? []), shelfDepthMm];
            }

            strokeRectangle(
                HORIZONTAL_PADDING_PX,
                startYpx,
                shelfDepthMm * pixelsPerMillimeter,
                shelfWidthMm * pixelsPerMillimeter
            );
        });
    });

    sectionsBySide("right").forEach((section, sectionIndex) => {
        const startYpx = VERTICAL_PADDING_PX + (section.offset + sectionIndex * ROW_GAP_PX) * pixelsPerMillimeter;
        const rightWallXpx = HORIZONTAL_PADDING_PX + roomLengthMm * pixelsPerMillimeter;

        normalizeToArray(section.items).forEach((shelf) => {
            const shelfWidthMm = Number(shelf?.width) || 0;
            const shelfDepthMm = Math.max(Number(shelf?.depth) || 0, 0);
            if (shelfWidthMm <= 0 || shelfDepthMm <= 0) return;

            if (!(renderedDepthsBySide?.[section.side] ?? []).includes(shelfDepthMm)) {
                drawDimensionInMillimeters(
                    svg,
                    rightWallXpx / pixelsPerMillimeter,
                    startYpx / pixelsPerMillimeter + shelfWidthMm / 2,
                    rightWallXpx / pixelsPerMillimeter - shelfDepthMm,
                    startYpx / pixelsPerMillimeter + shelfWidthMm / 2,
                    0,
                    `${shelfDepthMm}`,
                    pixelsPerMillimeter,
                    { fontPx: 6 }
                );
                renderedDepthsBySide[section.side] = [...(renderedDepthsBySide[section.side] ?? []), shelfDepthMm];
            }

            svg.rect(shelfDepthMm * pixelsPerMillimeter, shelfWidthMm * pixelsPerMillimeter)
                .move(rightWallXpx - shelfDepthMm * pixelsPerMillimeter, startYpx)
                .fill("none")
                .stroke({ width: SECTION_STROKE_PX, color: "#a64022" });
        });
    });
};
